import { baseApi } from '@/shared/api/baseApi';
import type {
  DebtsReportResult,
  ExpiryReportResult,
  NegativeStockReportResult,
  ProfitReportParams,
  ProfitReportResult,
  ProvisionalSalesReportResult,
  ReportDateRangeParams,
  SalesGroupBy,
  SalesProfitParams,
  SalesProfitResult,
  SalesReportResult,
  StockReportParams,
  StockReportResult,
  TurnoverReportResult,
  WriteOffsReportResult,
} from '../model/types';

function toQueryParams(params: object): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    out[key] = String(value);
  }
  return out;
}

const reportsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    stockReport: builder.query<StockReportResult, StockReportParams>({
      query: (params) => ({ url: 'reports/stock', params: toQueryParams(params) }),
      providesTags: ['CategoryStock', 'Report'],
    }),
    turnoverReport: builder.query<TurnoverReportResult, ReportDateRangeParams & { categoryId?: string; search?: string }>({
      query: (params) => ({ url: 'reports/turnover', params: toQueryParams(params) }),
      providesTags: ['Report'],
    }),
    salesReport: builder.query<SalesReportResult, ReportDateRangeParams & { groupBy: SalesGroupBy }>({
      query: (params) => ({ url: 'reports/sales', params: toQueryParams(params) }),
      providesTags: ['Report'],
    }),
    profitReport: builder.query<ProfitReportResult, ProfitReportParams>({
      query: (params) => ({ url: 'reports/profit', params: toQueryParams(params) }),
      providesTags: ['Report'],
    }),
    salesProfitReport: builder.query<SalesProfitResult, SalesProfitParams>({
      query: (params) => ({ url: 'reports/sales-profit', params: toQueryParams(params) }),
      providesTags: ['Report'],
    }),
    writeOffsReport: builder.query<WriteOffsReportResult, ReportDateRangeParams>({
      query: (params) => ({ url: 'reports/write-offs', params: toQueryParams(params) }),
      providesTags: ['Report'],
    }),
    debtsReport: builder.query<DebtsReportResult, void>({
      query: () => 'reports/debts',
      providesTags: ['Counterparty', 'Report'],
    }),
    expiryReport: builder.query<ExpiryReportResult, void>({
      query: () => 'reports/expiry',
      providesTags: ['CategoryStock', 'Report'],
    }),
    negativeStockReport: builder.query<NegativeStockReportResult, void>({
      query: () => 'reports/negative-stock',
      providesTags: ['CategoryStock', 'Report'],
    }),
    provisionalSalesReport: builder.query<ProvisionalSalesReportResult, ReportDateRangeParams>({
      query: (params) => ({ url: 'reports/provisional-sales', params: toQueryParams(params) }),
      providesTags: ['Report'],
    }),

    exportStockXlsx: builder.query<Blob, StockReportParams>({
      query: (params) => ({ url: 'reports/stock', params: { ...toQueryParams(params), format: 'xlsx' }, responseHandler: (r: Response) => r.blob() }),
    }),
    exportTurnoverXlsx: builder.query<Blob, ReportDateRangeParams & { categoryId?: string; search?: string }>({
      query: (params) => ({ url: 'reports/turnover', params: { ...toQueryParams(params), format: 'xlsx' }, responseHandler: (r: Response) => r.blob() }),
    }),
    exportSalesXlsx: builder.query<Blob, ReportDateRangeParams & { groupBy: SalesGroupBy }>({
      query: (params) => ({ url: 'reports/sales', params: { ...toQueryParams(params), format: 'xlsx' }, responseHandler: (r: Response) => r.blob() }),
    }),
    exportProfitXlsx: builder.query<Blob, ProfitReportParams>({
      query: (params) => ({ url: 'reports/profit', params: { ...toQueryParams(params), format: 'xlsx' }, responseHandler: (r: Response) => r.blob() }),
    }),
    exportSalesProfitXlsx: builder.query<Blob, SalesProfitParams>({
      query: (params) => ({ url: 'reports/sales-profit', params: { ...toQueryParams(params), format: 'xlsx' }, responseHandler: (r: Response) => r.blob() }),
    }),
    exportWriteOffsXlsx: builder.query<Blob, ReportDateRangeParams>({
      query: (params) => ({ url: 'reports/write-offs', params: { ...toQueryParams(params), format: 'xlsx' }, responseHandler: (r: Response) => r.blob() }),
    }),
    exportDebtsXlsx: builder.query<Blob, void>({
      query: () => ({ url: 'reports/debts', params: { format: 'xlsx' }, responseHandler: (r: Response) => r.blob() }),
    }),
    exportExpiryXlsx: builder.query<Blob, void>({
      query: () => ({ url: 'reports/expiry', params: { format: 'xlsx' }, responseHandler: (r: Response) => r.blob() }),
    }),
    exportNegativeStockXlsx: builder.query<Blob, void>({
      query: () => ({ url: 'reports/negative-stock', params: { format: 'xlsx' }, responseHandler: (r: Response) => r.blob() }),
    }),
    exportProvisionalSalesXlsx: builder.query<Blob, ReportDateRangeParams>({
      query: (params) => ({ url: 'reports/provisional-sales', params: { ...toQueryParams(params), format: 'xlsx' }, responseHandler: (r: Response) => r.blob() }),
    }),
  }),
});

export const {
  useStockReportQuery,
  useTurnoverReportQuery,
  useSalesReportQuery,
  useProfitReportQuery,
  useSalesProfitReportQuery,
  useWriteOffsReportQuery,
  useDebtsReportQuery,
  useExpiryReportQuery,
  useNegativeStockReportQuery,
  useProvisionalSalesReportQuery,
  useLazyExportStockXlsxQuery,
  useLazyExportTurnoverXlsxQuery,
  useLazyExportSalesXlsxQuery,
  useLazyExportProfitXlsxQuery,
  useLazyExportSalesProfitXlsxQuery,
  useLazyExportWriteOffsXlsxQuery,
  useLazyExportDebtsXlsxQuery,
  useLazyExportExpiryXlsxQuery,
  useLazyExportNegativeStockXlsxQuery,
  useLazyExportProvisionalSalesXlsxQuery,
} = reportsApi;

export { reportsApi };
